import React from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { FaShieldAlt, FaLock, FaCheckCircle, FaServer, FaGlobe, FaKey } from 'react-icons/fa';
import { theme } from '../styles/GlobalStyles';

const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const SecurityCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(97,93,255,0.15);
  }
`;

const IconContainer = styled.div`
  width: 52px; height: 52px;
  background: linear-gradient(135deg, rgba(97,93,255,0.2), rgba(61,217,235,0.1));
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 1.25rem;
  font-size: 1.3rem;
  background-color: transparent;

  svg {
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const CardTitle = styled.h3`font-size:1.1rem;color:${theme.text};margin-bottom:0.6rem;`;
const CardDesc  = styled.p`font-size:0.875rem;color:${theme.textMuted};line-height:1.65;margin:0;`;

const ComplianceSection = styled.div`
  margin-top: 5rem;
`;

const ComplianceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-top: 2.5rem;
`;

const ComplianceBadge = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(97,93,255,0.35);
    background: ${theme.cardBgHover};
    transform: translateY(-4px);
  }
`;

const BadgeName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.text};
  margin-bottom: 0.5rem;
`;

const BadgeDesc = styled.div`
  font-size: 0.78rem;
  color: ${theme.textDim};
  line-height: 1.5;
`;

const DataSection = styled.div`
  margin-top: 5rem;
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(14px);

  p { color: ${theme.textMuted}; line-height: 1.7; margin-bottom: 1rem; }
  p:last-child { margin-bottom: 0; }
`;

const DataTitle = styled.h3`
  font-size: 1.6rem;
  background: ${theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
`;

const CtaSection = styled.div`
  margin-top: 5rem;
  background: ${theme.cardBg};
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 20px;
  padding: 3.5rem;
  text-align: center;
  backdrop-filter: blur(14px);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 50%; height: 1px;
    background: linear-gradient(to right, transparent, rgba(97,93,255,0.5), transparent);
  }

  h3 { font-size: 1.8rem; color: ${theme.text}; margin-bottom: 1rem; }
  p  { color: ${theme.textMuted}; max-width: 560px; margin: 0 auto 2rem; font-size: 1rem; }
`;

const securityFeatures = [
  { icon: <FaLock />,         title: 'End-to-End Encryption',    desc: 'All data transmitted between our servers and clients is encrypted using industry-standard TLS 1.3 encryption.' },
  { icon: <FaKey />,          title: 'Cryptographic QR Codes',   desc: 'Each QR code contains encrypted data signed with a unique key, making duplication cryptographically impossible.' },
  { icon: <FaServer />,       title: 'Secure Cloud Infrastructure', desc: 'Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA, redundant systems, and automatic failover.' },
  { icon: <FaShieldAlt />,    title: 'Fraud Detection',          desc: 'AI-powered anomaly detection identifies suspicious scan patterns, alerting brands to potential counterfeit activity in real time.' },
  { icon: <FaCheckCircle />,  title: 'Regular Security Audits',  desc: 'Our systems undergo regular third-party security audits and penetration testing to identify and address vulnerabilities.' },
  { icon: <FaGlobe />,        title: 'Global CDN',               desc: 'Verification requests are served via a global CDN ensuring fast response times worldwide — typically under 200ms.' },
];

const certifications = [
  { name: 'ISO 27001',  desc: 'Information Security Management' },
  { name: 'SOC 2 Type II', desc: 'Security & Availability Controls' },
  { name: 'GDPR',       desc: 'EU Data Protection Compliant' },
  { name: 'CCPA',       desc: 'California Consumer Privacy Act' },
  { name: 'PCI DSS',    desc: 'Payment Card Industry Standards' },
  { name: 'HIPAA',      desc: 'Health Data Compliance Ready' },
];

const TrustCompliancePage = () => (
  <>
    <PageBanner
      title="Trust & Compliance"
      subtitle="Security and compliance are at the foundation of everything we build at Prodseal."
    />

    <ContentSection>
      <SectionTitle>Enterprise-Grade Security</SectionTitle>
      <SecurityGrid>
        {securityFeatures.map(({ icon, title, desc }) => (
          <SecurityCard key={title}>
            <IconContainer>{icon}</IconContainer>
            <CardTitle>{title}</CardTitle>
            <CardDesc>{desc}</CardDesc>
          </SecurityCard>
        ))}
      </SecurityGrid>

      <ComplianceSection>
        <SectionTitle>Certifications & Compliance</SectionTitle>
        <ComplianceGrid>
          {certifications.map(({ name, desc }) => (
            <ComplianceBadge key={name}>
              <BadgeName>{name}</BadgeName>
              <BadgeDesc>{desc}</BadgeDesc>
            </ComplianceBadge>
          ))}
        </ComplianceGrid>
      </ComplianceSection>

      <DataSection>
        <DataTitle>Our Data Privacy Promise</DataTitle>
        <p>
          At Prodseal, we take data privacy seriously. We collect only the data necessary to
          provide our authentication and warranty services, and we never sell personal
          information to third parties.
        </p>
        <p>
          All consumer verification data is processed in compliance with applicable privacy
          regulations including GDPR and CCPA. Brands receive aggregated analytics data
          while individual consumer privacy is maintained.
        </p>
        <p>
          We retain scan data for the period necessary to provide our services and meet
          legal obligations. Consumers can request deletion of their data at any time
          through our privacy portal.
        </p>
      </DataSection>

      <CtaSection>
        <h3>Have Security Questions?</h3>
        <p>
          Our security team is available to discuss your specific compliance requirements
          and how Prodseal can meet your organisation's security standards.
        </p>
        <Button primary to="/contact">Contact Our Security Team</Button>
      </CtaSection>
    </ContentSection>
  </>
);

export default TrustCompliancePage;
